import './Tabs.scss';
import React, {
    createRef,
    MouseEvent,
    ReactElement,
    ReactNode,
    RefObject,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { ResizePayload, useResizeDetector } from 'react-resize-detector';
import { useLocation, useNavigate } from 'react-router';
import { IMenuContext, ITab } from '@/types';
import clsx from 'clsx';
import Menu from '../Menu/Menu';
import { MenuContext } from '../Menu/MenuContext';
import Button from '../Button';
import { MdMenu } from 'react-icons/md';
import DocumentTabLabel, {
    IDocumentTab,
} from '@organisms/document/DocumentTabBuilder/tabs/DocumentTabLabel/DocumentTabLabel';
import DocumentTabContent from '@organisms/document/DocumentTabBuilder/tabs/DocumentTabContent/DocumentTabContent';
import DocumentTabMenuContainer from '@organisms/document/DocumentTabBuilder/tabs/DocumentTabMenuContainer/DocumentTabMenuContainer';
import { TabsContext } from '@/context/Contexts';

export interface ITabsProps {
    /** Список вкладок */
    list: ITab[];
    /**
     * @default true
     * Показывать линию под табами
     * */
    showLine?: boolean;
    /**
     * Показывать меню если табы не вмещаются в контейре
     * @default true
     */
    showMenu?: boolean;
    /**
     * Размер табов
     * @default 'md'
     */
    size?: 'md' | 'sm';
    /**
     * Ренедерить неактивные табы
     * @default true
     */
    renderInactiveTabs?: boolean;

    /** Если во вкладках есть url, то через children пробрасывается <Router/> */
    children?: ReactNode | ReactNode[];

    onTabIndexChanged?: (tabIndex: number) => void;
    /** Класс */
    className?: string;
}

const Tabs: React.FC<ITabsProps> = ({
    list,
    showLine = true,
    showMenu = true,
    size = 'md',
    renderInactiveTabs = true,
    children,
    onTabIndexChanged,
    className,
}: ITabsProps) => {
    const router = useNavigate();
    const location = useLocation();
    /** Ссылки на вкладки */
    const refs = useRef<RefObject<HTMLDivElement | null>[]>([]);
    const currentUrl = useRef<number>(0);
    /** Последний индекс таба, который показан */
    const [lastVisibleIndex, setLastVisibleIndex] = useState<number>(list.length - 1);

    /** Определяем, если вкладки являются ссылками для роутинга */
    const isRouting: boolean = useMemo(() => list.every((t: ITab) => t.url), [list]);

    /** Устанавливаем индекс последнего видимого таба */
    const onResize = useCallback(
        ({ width }: ResizePayload) => {
            handleLastVisibleIndex(width ?? 0);
        },
        [showMenu, list],
    );

    const { ref, width } = useResizeDetector<HTMLDivElement>({
        onResize,
        refreshMode: 'debounce',
        refreshRate: 300,
    });

    useEffect(() => {
        if (!showMenu) {
            setLastVisibleIndex(list.length - 1);
        }
    }, [showMenu, list.length]);

    useEffect(() => {
        setActive((i: number) => {
            const index = list.findIndex((t: ITab) => {
                if (isRouting && t.url && (location.hash !== '' || location.pathname.includes('#'))) {
                    if (t.url.startsWith('#')) {
                        return t.exact ? t.url === location.hash : location.hash.includes(t.url);
                    } else {
                        return t.exact ? t.url === location.pathname : location.pathname.includes(t.url);
                    }
                }

                return t.active;
            });

            const indexTab = index >= 0 && !list[index].disabled ? index : i;
            currentUrl.current = indexTab;

            return indexTab;
        });
    }, [list, isRouting, location]);

    // -------------------------------------------------------------------------------------------------------------------

    /** Определяем ширину таба */
    const getWidthTab = (element: HTMLDivElement | null) => element?.getBoundingClientRect().width || 0;
    const visTabs: { [id: number]: boolean } = {};

    /** Получаем индекс последнего видимого таба */
    const handleLastVisibleIndex = (containerWidth = 0) => {
        if (!showMenu) {
            return;
        }

        const MENU_WIDTH = 48;
        const hasHiddenIndex = lastVisibleIndex < list.length - 1;
        let visibleIndex = lastVisibleIndex;
        let visibleWidth = hasHiddenIndex ? MENU_WIDTH : 0;

        for (let index = 0; index < list.length; index++) {
            if (refs.current?.[index]?.current) {
                const w = getWidthTab(refs.current[index].current);
                if (containerWidth > visibleWidth + w) {
                    visibleWidth += w;
                } else {
                    break;
                }
            }
            visibleIndex = index;
        }

        /** Определяем показывать ли следующий скрытый таб */
        if (hasHiddenIndex) {
            const nextIndex = visibleIndex + 1;
            const isLastIndex = nextIndex === refs.current.length - 1;
            const nextWidth = getWidthTab(refs.current?.[nextIndex]?.current);

            if (visibleWidth + nextWidth - (isLastIndex ? MENU_WIDTH : 0) <= containerWidth) {
                visibleWidth += isLastIndex ? nextWidth : nextWidth + MENU_WIDTH;
                visibleIndex = nextIndex;
            }
        }

        setLastVisibleIndex(visibleIndex);
    };

    // -------------------------------------------------------------------------------------------------------------------

    /** Определяем активную вкладку */
    const [active, setActive] = useState<number>(0);

    const [loadedTabs, setLoadedTabs] = useState<number[]>([]);

    /** Определяем является ли активный таб в меню */
    const isActiveIndexInMenu: boolean = active > lastVisibleIndex;

    /** Управление полоской */
    const [lineStyle, setLineStyle] = useState<{ left: number; width: number }>({ left: 0, width: 0 });
    const setLinePosition = (element: HTMLDivElement) => {
        if (isActiveIndexInMenu) {
            if (lineStyle.left !== 0 || lineStyle.width !== 0) {
                setLineStyle({ left: 0, width: 0 });
            }
        } else if (element) {
            const left = element.offsetLeft;
            const width = element.offsetWidth;
            if (lineStyle.left !== left || lineStyle.width !== width) {
                setLineStyle({ left, width });
            }
        }
    };

    /** Устанавливаем активную вкладку */
    const onClick = (e: MouseEvent, i: number, element: HTMLDivElement | null) => {
        element && setLinePosition(element);

        if (isRouting && list[i].url && active !== i) {
            let locState = location.state as any;
            router(list[i].url as string, {
                state: {
                    fromPathname: locState?.fromPathname ?? null,
                    fromSearch: locState?.fromSearch ?? null,
                    fromHash: locState?.fromHash ?? null,
                },
            });
        }

        setActive(i);
        if (!loadedTabs.includes(i)) {
            setLoadedTabs((prev) => [...prev, i]);
        }
        if (onTabIndexChanged) onTabIndexChanged(i);

        list[i].handler?.(refs.current[i].current as HTMLDivElement);
    };

    // -------------------------------------------------------------------------------------------------------------------
    const visibilityEvent = (index: number, visibilityStatus: boolean) => {
        visTabs[index] = visibilityStatus;
        if (currentUrl.current === index && !visibilityStatus) {
            for (let i = 0; i < index; i++) {
                if (visTabs[i]) {
                    setActive(i);
                    currentUrl.current = i;
                    break;
                }
            }
        }
        if (active == index && visibilityStatus == false) {
            if (refs.current[active]?.current) {
                const element = refs.current[active]!.current!;
                setLinePosition(element);
            } else {
                const i = refs.current.findIndex((x) => x.current != null);
                if (i > -1) {
                    setActive(i);
                    if (!loadedTabs.includes(i)) {
                        setLoadedTabs((prev) => [...prev, i]);
                    }
                    if (onTabIndexChanged) onTabIndexChanged(i);

                    list[i].handler?.(refs.current[i].current as HTMLDivElement);
                }
            }
        }
    };

    /** Список вкладок */
    const nav = list.map((t: ITab, i: number) => {
        if (!refs.current[i]) {
            refs.current[i] = createRef();
        }

        const onMenuClickHandler = (e: MouseEvent) => onClick(e, i, refs.current[i].current);
        let docTab = t as IDocumentTab;

        return docTab.includedFields ? (
            <DocumentTabLabel
                key={i}
                tab={docTab}
                index={i}
                selected={active}
                lastVisibleIndex={lastVisibleIndex}
                size={size}
                refTab={refs.current[i]}
                mouseHandler={onMenuClickHandler}
                visibilityEvent={visibilityEvent}
            />
        ) : (
            <div
                key={i}
                className={clsx('rf-tabs__link', i > lastVisibleIndex && 'rf-tabs__link--hidden')}
                ref={refs.current[i]}
            >
                <button
                    type="button"
                    className={clsx(
                        'rf-tabs__button',
                        i === active && 'rf-tabs__button--active',
                        `rf-tabs__button--${size}`,
                    )}
                    disabled={t.disabled}
                    onClick={onMenuClickHandler}
                >
                    {!!t.icon && <div className="rf-tabs__icon">{t.icon}</div>}
                    {!!t.label && <div>{t.label}</div>}
                </button>
            </div>
        );
    });

    /** вкладки */
    const tabsContent = renderInactiveTabs ? (
        list.map((t: ITab, i: number) => {
            let docTab = t as IDocumentTab;
            return docTab.includedFields ? (
                <div key={i} className={clsx('rf-tabs__content_box', i != active && 'rf-tabs__content_hide')}>
                    <DocumentTabContent tab={docTab} />
                </div>
            ) : (
                <div key={i} className={clsx('rf-tabs__content_box', i != active && 'rf-tabs__content_hide')}>
                    {i == active || loadedTabs.indexOf(i) > -1 ? t.tab : <></>}
                </div>
            );
        })
    ) : (
        <div className={clsx('rf-tabs__content_box')}>{list[active]?.tab}</div>
    );

    /** Список вкладок в меню */
    const menuNav = (
        <MenuContext.Consumer>
            {({ onClose }: IMenuContext) =>
                list.reduce((acc: ReactElement[], curr: ITab, i: number) => {
                    let isDocTab = (curr as IDocumentTab).formMethods !== undefined;
                    if (i > lastVisibleIndex) {
                        const onMenuClickHandler = (e: MouseEvent) => {
                            onClick(e, i, refs.current[i].current);
                            onClose();
                        };

                        let menuContent = (
                            <button
                                type="button"
                                className={clsx(
                                    'rf-tabs__button',
                                    'rf-tabs__menu-button',
                                    i === active && 'rf-tabs__menu-button--active',
                                )}
                                disabled={curr.disabled}
                                onClick={onMenuClickHandler}
                            >
                                {!!curr.icon && (
                                    <div className={clsx('rf-tabs__icon', 'rf-tabs__menu-icon')}>{curr.icon}</div>
                                )}
                                {!!curr.label && <div>{curr.label}</div>}
                            </button>
                        );
                        return [
                            ...acc,
                            <div key={i} className={clsx('rf-tabs__link', 'rf-tabs__menu-link')}>
                                {isDocTab ? (
                                    <DocumentTabMenuContainer tab={curr as IDocumentTab} children={menuContent} />
                                ) : (
                                    menuContent
                                )}
                            </div>,
                        ];
                    }

                    return acc;
                }, [])
            }
        </MenuContext.Consumer>
    );

    /** Определяем есть ли табы в меню */
    const hasMenuNav = lastVisibleIndex < list.length - 1;

    useLayoutEffect(() => {
        // при изменении ширины/списка/режима меню - пересчитать видимые табы
        if (showMenu) {
            handleLastVisibleIndex(width ?? 0);
        } else if (lastVisibleIndex !== list.length - 1) {
            setLastVisibleIndex(list.length - 1);
        }

        // поставить линию под активный таб после layout
        const el = refs.current[active]?.current;
        if (!el) return;

        const rafId = requestAnimationFrame(() => {
            setLinePosition(el);
        });
        return () => cancelAnimationFrame(rafId);
    }, [active, list, showMenu, width]);

    // -------------------------------------------------------------------------------------------------------------------

    return (
        <TabsContext
            value={{
                activeTab: active,
            }}
        >
            <div className={clsx('rf-tabs', 'rf-tabs--buttons', className)} ref={ref}>
                <nav className={clsx('rf-tabs__navigation', showLine && 'rf-tabs__navigation__line')}>
                    <div
                        className={clsx(
                            'rf-tabs__navigation-list',
                            !showMenu && 'rf-tabs__navigation-list--without-menu',
                        )}
                    >
                        {nav}
                        {hasMenuNav && (
                            <Menu
                                content={<div className="rf-tabs__menu-content">{menuNav}</div>}
                                position="bottom-start"
                                className={clsx('rf-tabs__menu', isActiveIndexInMenu && 'rf-tabs__menu--active')}
                            >
                                <Button
                                    buttonType="ghost"
                                    size="xs"
                                    data-testid="button-menu-group__more"
                                    className="rf-tabs__menu__btn"
                                    startAdornment={<MdMenu />}
                                />
                            </Menu>
                        )}
                    </div>
                    <div
                        className="rf-tabs__navigation-line"
                        style={{
                            left: `${lineStyle.left}px`,
                            width: `${lineStyle.width}px`,
                        }}
                    />
                </nav>

                {(children || list.length > 0) && (
                    <div className="rf-tabs__content">{children ? children : tabsContent}</div>
                )}
            </div>
        </TabsContext>
    );
};

export default Tabs;
